// src/App.js

import { useEffect, useMemo } from 'react';

import 'react-toastify/dist/ReactToastify.css';
import './styles/style.css';

import { useSelector } from 'react-redux';

import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, ro, ru } from 'date-fns/locale';
import i18next from 'i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Box, CircularProgress, CssBaseline, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as locales from '@mui/material/locale';

import { appURL } from './components/Data/baseURL';
import { useRetrieveUserQuery } from './redux/features/authApi';
import useVerify from './hooks/use-verify';
import Account from './components/Pages/Account';
import ActivateUser from './components/Pages/Authentication/ActivateUser';
import AllCountries from './components/Pages/AllCountries';
import BankAccounts from './components/Apm/BankAccount/BankAccounts';
import Base from './components/Pages/Base';
import Company from './components/Pages/Company';
import Contacts from './components/Pages/Contacts';
import ContractTerms from './components/Apm/Term/ContractTerms';
import Currencies from './components/Apm/Currency/Currencies';
import Dashboard from './components/Pages/Dashboard';
import Faq from './components/Apm/Faq/Faq';
import Header from './components/Header/Header';
import ItemsInv from './components/Apm/ItemInv/ItemsInv';
import LogIn from './components/Pages/Authentication/LogIn';
import MyInvs from './components/Pages/MyInvs';
import MyQuotes from './components/Pages/MyQuotes';
import NotFound from './components/Pages/NotFound';
import PassReset from './components/Pages/Authentication/PassReset';
import PassResetConfirm from './components/Pages/Authentication/PassResetConfirm';
import Products from './components/Pages/Products';
import Settings from './components/Pages/Settings';
import SignUp from './components/Pages/Authentication/SignUp';
import Subscription from './components/Pages/Subscription';
import Support from './components/Apm/Other/Support';
import TermsConditions from './components/Apm/Faq/TermsConditions';
import TermsOfPayment from './components/Apm/Term/TermsOfPayment';
import Users from './components/Pages/Users';
import ReferenceDataBootstrap from './components/Apm/ReferenceDataBootstrap';

const SUPPORTED_LANGUAGES = new Set(['en', 'ro', 'ru']);

const LANGUAGE_CONFIG = {
  en: {
    muiLocale: 'enUS',
    dateLocale: enGB,
  },

  ro: {
    muiLocale: 'roRO',
    dateLocale: ro,
  },

  ru: {
    muiLocale: 'ruRU',
    dateLocale: ru,
  },
};

const normalizeLanguage = (value) => {
  const rawValue = value && typeof value === 'object' ? value.value : value;

  const languageCode = String(rawValue || '')
    .split('-')[0]
    .toLowerCase();

  return SUPPORTED_LANGUAGES.has(languageCode) ? languageCode : 'ro';
};

const FullPageLoader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

/**
 * Authentication is already checked by useVerify() in App.
 * This component only protects the route based on Redux auth state.
 */
const RequireAuth = () => {
  const location = useLocation();

  const { isAuthenticated, isAuthLoading } = useSelector((state) => state.authSlice);

  if (isAuthLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`${appURL}/login`}
        state={{
          from: location,
        }}
        replace
      />
    );
  }

  return <Outlet />;
};

function App() {
  /*
   * Perform the initial cookie-session verification once.
   */
  useVerify();

  const { isAuthenticated } = useSelector((state) => state.authSlice);

  /*
   * Load account settings only after authentication succeeds.
   * This replaces state.accountSlice.account.
   */
  const { data: account } = useRetrieveUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  const storedLanguage = localStorage.getItem('i18nextLng');

  const fallbackLanguage = normalizeLanguage(i18next.resolvedLanguage || i18next.language || storedLanguage || 'ro');

  const selectedLanguage = normalizeLanguage(account?.lang || fallbackLanguage);

  const languageConfig = LANGUAGE_CONFIG[selectedLanguage];

  useEffect(() => {
    const currentLanguage = normalizeLanguage(i18next.resolvedLanguage || i18next.language);

    if (currentLanguage !== selectedLanguage) {
      void i18next.changeLanguage(selectedLanguage);
    }
  }, [selectedLanguage]);

  const theme = useMemo(() => {
    const muiLocale = locales[languageConfig.muiLocale] || locales.roRO;

    return createTheme(
      {
        palette: {
          primary: {
            main: '#0EA5E9',
            dark: '#0369A1',
            light: '#7DD3FC',
          },
          secondary: {
            main: '#7C3AED',
          },
          background: {
            default: '#F4F7FB',
            paper: '#FFFFFF',
          },
          baseblue: {
            main: '#043873',
            light: '#4F9CF9',
          },
          baseyellow: {
            main: '#FFE492',
          },
        },
        typography: {
          fontFamily: "Inter, Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          button: {
            fontWeight: 750,
          },
        },
        shape: {
          borderRadius: 10,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              rounded: {
                borderRadius: 14,
              },
            },
          },
        },
      },
      muiLocale
    );
  }, [languageConfig.muiLocale]);

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={languageConfig.dateLocale}>
          <BrowserRouter>
            <CssBaseline />

            <ReferenceDataBootstrap />

            <ToastContainer
              position="top-center"
              autoClose={2500}
              draggable
              pauseOnHover
              style={{
                width: smMediaDown ? undefined : 500,

                marginTop: 0,
              }}
              theme="dark"
              transition={Slide}
            />

            <Header>
              <Routes>
                {/* Public routes */}

                <Route path="/" element={<Base />} />

                <Route path={`${appURL}/login`} element={<LogIn />} />

                <Route path={`${appURL}/signup`} element={<SignUp />} />

                <Route path="/api/activate/:uid/:token" element={<ActivateUser />} />

                <Route path={`${appURL}/passreset`} element={<PassReset />} />

                <Route path="/api/password/reset/confirm/:uid/:token" element={<PassResetConfirm />} />

                <Route path={`${appURL}/terms-conditions`} element={<TermsConditions />} />

                <Route path={`${appURL}/faq`} element={<Faq />} />

                <Route path={`${appURL}/support`} element={<Support />} />

                <Route path={`${appURL}/products`} element={<Products />} />

                {/* Protected routes */}

                <Route element={<RequireAuth />}>
                  <Route path={`${appURL}/account`} element={<Account />} />

                  <Route path={`${appURL}/subscription`} element={<Subscription />} />

                  <Route path={`${appURL}/settings`} element={<Settings />} />

                  <Route path={`${appURL}/users`} element={<Users />} />

                  <Route path={appURL} element={<Dashboard />} />

                  <Route path={`${appURL}/quotes`} element={<MyQuotes />} />

                  <Route path={`${appURL}/invoices`} element={<MyInvs />} />

                  <Route path={`${appURL}/clients`} element={<Contacts />} />

                  <Route path={`${appURL}/items-for-item-inv`} element={<ItemsInv />} />

                  <Route path={`${appURL}/company`} element={<Company />} />

                  <Route path={`${appURL}/bank-accounts`} element={<BankAccounts />} />

                  <Route path={`${appURL}/terms`} element={<ContractTerms />} />

                  <Route path={`${appURL}/terms-of-payment`} element={<TermsOfPayment />} />

                  <Route path={`${appURL}/countries`} element={<AllCountries />} />

                  <Route path={`${appURL}/currencies`} element={<Currencies />} />
                </Route>

                <Route path="/not-found" element={<NotFound />} />

                <Route path="*" element={<Navigate to="/not-found" replace />} />
              </Routes>
            </Header>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
